import {useEffect} from 'react'
import PropTypes from 'prop-types'

import {PauseIcon, PlayIcon} from './icons'
import {formatDuration} from './lifeStoryTools'
import useUploadAudio from './hooks/useUploadAudio'

const PlaybackRow = ({label, recording, isNew, track, onPlay, onToggle}) => {
  const uploadId = recording['upload-id']
  const active = track && track.uploadId === uploadId
  const playing = Boolean(active && track.playing)
  const duration = (active && track.duration) || recording.duration || 0
  const progress = active && duration ? Math.min(100, (track.elapsed / duration) * 100) : 0

  return (
    <div className={`ls-pv-row${isNew ? ' new' : ''}${playing ? ' playing' : ''}`}>
      <button
        aria-label={`${playing ? 'Pause' : 'Play'} ${label.toLowerCase()}`}
        aria-pressed={playing}
        className='ls-pv-play'
        type='button'
        onClick={() => (active ? onToggle() : onPlay({uploadId, filename: recording.filename, duration: recording.duration}))}
      >
        {playing ? <PauseIcon size={18} /> : <PlayIcon size={18} />}
      </button>
      <span className='ls-pv-meta'>
        <span className='ls-pv-t'>{label}</span>
        <span className='ls-pv-d'>{active && playing ? `${formatDuration(track.elapsed)} / ` : ''}{formatDuration(duration)}</span>
      </span>
      <span aria-hidden='true' className='ls-pv-bar'><i style={{width: `${progress}%`}} /></span>
    </div>
  )
}

const recordingShape = PropTypes.shape({
  duration: PropTypes.number,
  filename: PropTypes.string,
  'upload-id': PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
})

PlaybackRow.propTypes = {
  isNew: PropTypes.bool,
  label: PropTypes.string.isRequired,
  onPlay: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  recording: recordingShape.isRequired,
  track: PropTypes.object
}

/**
 * Both takes with their own Play (TK-3204 "Record this version"): listening
 * is one track at a time, and unmounting stops it. The takeover shows the
 * rows in its body with the choice in its footer, so they are separate.
 */
export const PlaybackRows = ({current, take}) => {
  const playback = useUploadAudio()

  return (
    <div className='ls-pv-list'>
      <PlaybackRow isNew label='New recording' recording={take} track={playback.track} onPlay={playback.play} onToggle={playback.togglePlayback} />
      <PlaybackRow label='Current recording' recording={current} track={playback.track} onPlay={playback.play} onToggle={playback.togglePlayback} />
    </div>
  )
}

PlaybackRows.propTypes = {
  current: recordingShape.isRequired,
  take: recordingShape.isRequired
}

/**
 * Keep my current recording / Use new recording. `wide` is the takeover's
 * full-width footer on phones, where Use comes first. Use takes focus on
 * mount, as the prototype does when the question appears.
 */
export const DecisionButtons = ({wide, useRef: useButtonRef, onKeep, onUse}) => {
  useEffect(() => {
    if (useButtonRef && useButtonRef.current) useButtonRef.current.focus()
  }, [useButtonRef])

  return (
    <>
      <button className={wide ? 'ls-f-text wide-m' : 'ls-txt-btn'} type='button' onClick={onKeep}>Keep my current recording</button>
      <button className={wide ? 'ls-f-go wide-m' : 'ls-done-btn'} ref={useButtonRef} type='button' onClick={onUse}>Use new recording</button>
    </>
  )
}

DecisionButtons.propTypes = {
  onKeep: PropTypes.func.isRequired,
  onUse: PropTypes.func.isRequired,
  useRef: PropTypes.shape({current: PropTypes.any}),
  wide: PropTypes.bool
}

/** "Use your new recording?" as the modal shows it: rows, then the choice. */
const RecordingDecision = ({current, take, useRef: useButtonRef, onKeep, onUse}) => (
  <>
    <PlaybackRows current={current} take={take} />
    <div className='ls-rv-decide'>
      <DecisionButtons useRef={useButtonRef} onKeep={onKeep} onUse={onUse} />
    </div>
  </>
)

RecordingDecision.propTypes = {
  current: recordingShape.isRequired,
  onKeep: PropTypes.func.isRequired,
  onUse: PropTypes.func.isRequired,
  take: recordingShape.isRequired,
  useRef: PropTypes.shape({current: PropTypes.any})
}

export default RecordingDecision
